import { observer } from "mobx-react";
import { useStyles } from "./CompanyOverviewBox.styles";
import { useStore } from "src/Shared/Hooks";
import { Paper, Typography, Grid, Box, Divider, Button } from "@mui/material";
import { Link } from "react-router-dom";
import OnboardingCard from "../OnboardingCard/OnboardingCard";
import DangerZoneCard from "../DangerZoneCard/DangerZoneCard";
import { isClientArchived } from "../../Tools/ArchivedClient";
import { EMPTY_VALUE, isAbsent } from "../../Tools/DisplayValue";

// The emptiness rule and the token both live in Tools/DisplayValue so this page, the record banner
// and the SOP builder cannot drift apart again (bug 5840). The value itself is returned unchanged —
// several callers below pass through non-string values.
const displayValue = (val: any) => (isAbsent(val) ? EMPTY_VALUE : val);

const formatCurrency = (val: number | null) => {
  if (val === null) return "—";
  return `$${val.toLocaleString(undefined, {
    minimumFractionDigits: 2,
  })}`;
};

const CompanyOverviewBox: React.FC = () => {
  const classes = useStyles();
  const {
    rootStore: {
      companyStore: { companyOverview: overview },
      commonStore: { clientStatus },
    },
  } = useStore();
  return (
    <>
      <Box className={classes.root}>
        <Typography className={classes.title} variant="h5">
          Overview
        </Typography>
      </Box>
      {/* COMPANY INFORMATION */}
      {overview && (
        <>
          {/* Both cards act on the client — the onboarding card starts, continues and promotes, the
              danger zone deletes — so neither is offered on a record that has already been archived
              and is kept only to be read (CR.11). */}
          {!isClientArchived(overview) && <OnboardingCard />}
          <Paper elevation={0} className={classes.card}>
            <Grid container spacing={3}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={6}>
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Company Name
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.companyName)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    TDDA Supporting Region
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.franchiseName)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    National / Regional
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.companyType)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Website</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.website)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Lead Type</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.leadType)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Client Deal Value
                  </Typography>
                  <Typography className={classes.value}>
                    {formatCurrency(overview.salesTotalValue)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>SLA Status</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.slaStatus)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    SLA Expiry Date
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.slaExpiryDate)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Status</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.status)}
                  </Typography>
                </Box>

                {/* The Linked to Imperans flag, mirrored read-only (CR.7).
                    The Overview had no such line at all, so the flag had nowhere to show: the only
                    occurrence of "Imperans" on the page was the sidebar item (bug 5706). The store
                    reloads the overview when the flag is toggled and this component observes it, so
                    the line updates without a reload. The linked client's name is shown beneath when
                    there is one, which is what makes the line worth reading. */}
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Imperans Integration
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.companyIntegrationStatus)}
                  </Typography>
                  {overview.companyIntegrationDetail?.label && (
                    <Typography variant="caption" color="text.secondary">
                      Linked to {overview.companyIntegrationDetail.label}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    TDDA Supporting Clinic
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.franchiseOfficeName)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Industry</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.industry)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Total Employees
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.totalStaffNumber)}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Services</Typography>
                  <Typography className={classes.value}>
                    {overview.services?.length
                      ? overview.services.join(", ")
                      : "—"}
                  </Typography>
                </Box>

                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Special Pricing
                  </Typography>
                  <Typography className={classes.value}>
                    {overview?.specialPricing ? "Yes" : "No"}
                  </Typography>
                </Box>
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    Price Review Date
                  </Typography>
                  <Typography className={classes.value}>
                    {overview?.priceReviewDate ?? "—"}
                  </Typography>
                </Box>
                {/* Read-only mirror of the main contact (CR.2); Manage contacts on the
                    onboarding card switches to the Contacts section to edit it. */}
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>Main Contact</Typography>
                  <Typography className={classes.value}>
                    {displayValue(overview.primaryContactName)}
                  </Typography>
                </Box>
                <Box className={classes.fieldWrapper}>
                  <Typography className={classes.label}>
                    SLA Primary Relationship Holder
                  </Typography>
                  <Typography className={classes.value}>
                    {displayValue(
                      overview?.slaPrimaryCompanyRelationshipManager,
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          {!isClientArchived(overview) && <DangerZoneCard />}
        </>
      )}
    </>
  );
};

export default observer(CompanyOverviewBox);
